import { parse, format, startOfWeek, endOfWeek } from 'date-fns';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, ToggleLeft, ToggleRight, Info, Play } from 'lucide-react';
import { useOpsStore } from '../../store/ops.store';
import DatePicker from '../DatePicker';

const viewTabs = [
  { key: 'executive' as const, label: 'Tổng quan' },
  { key: 'heatmap' as const, label: 'Heatmap' },
  { key: 'quality' as const, label: 'Chất lượng' },
  { key: 'internal-control' as const, label: 'Kiểm soát nội bộ' },
  { key: 'config' as const, label: 'Cấu hình' },
];

const validViews = ['executive', 'heatmap', 'quality', 'internal-control', 'config'] as const;

export default function DashboardFilters({ isDark }: { isDark: boolean }) {
  const {
    fromDate,
    toDate,
    excludeAtHome,
    activeView,
    setDateRange,
    toggleAtHome,
    setActiveView,
    fetchDashboardData,
  } = useOpsStore();

  const [searchParams, setSearchParams] = useSearchParams();

  // Sync URL → store on mount
  useEffect(() => {
    const viewParam = searchParams.get('view') as typeof activeView | null;
    if (viewParam && validViews.includes(viewParam) && viewParam !== activeView) {
      setActiveView(viewParam);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fromDateObj = parse(fromDate, 'yyyy-MM-dd', new Date());
  const toDateObj = parse(toDate, 'yyyy-MM-dd', new Date());

  const now = new Date();
  const thisMonday = startOfWeek(now, { weekStartsOn: 1 });
  const thisSunday = endOfWeek(now, { weekStartsOn: 1 });
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);
  const lastSunday = endOfWeek(lastMonday, { weekStartsOn: 1 });
  const twoWeeksAgoMonday = new Date(thisMonday);
  twoWeeksAgoMonday.setDate(thisMonday.getDate() - 14);
  const twoWeeksAgoSunday = endOfWeek(twoWeeksAgoMonday, { weekStartsOn: 1 });
  const nextMonday = new Date(thisMonday);
  nextMonday.setDate(thisMonday.getDate() + 7);
  const nextSunday = endOfWeek(nextMonday, { weekStartsOn: 1 });

  const isSelected = (monday: Date, sunday: Date) =>
    fromDate === format(monday, 'yyyy-MM-dd') &&
    toDate === format(sunday, 'yyyy-MM-dd');

  const selectWeek = (monday: Date, sunday: Date) => {
    setDateRange(format(monday, 'yyyy-MM-dd'), format(sunday, 'yyyy-MM-dd'));
  };

  const handleFromChange = (date: Date | null) => {
    if (!date) return;
    setDateRange(format(date, 'yyyy-MM-dd'), toDate);
  };

  const handleToChange = (date: Date | null) => {
    if (!date) return;
    setDateRange(fromDate, format(date, 'yyyy-MM-dd'));
  };

  const handleToggle = () => {
    toggleAtHome();
    setTimeout(() => useOpsStore.getState().fetchDashboardData(), 0);
  };

  const handleViewChange = (view: typeof activeView) => {
    setActiveView(view);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (view === 'executive') {
        next.delete('view');
      } else {
        next.set('view', view);
      }
      return next;
    }, { replace: true });
    setTimeout(() => useOpsStore.getState().fetchDashboardData(), 0);
  };

  const weekPresets = [
    { label: 'Tuần kia', monday: twoWeeksAgoMonday, sunday: twoWeeksAgoSunday },
    { label: 'Tuần rồi', monday: lastMonday, sunday: lastSunday },
    { label: 'Tuần này', monday: thisMonday, sunday: thisSunday },
    { label: 'Tuần tới', monday: nextMonday, sunday: nextSunday },
  ];

  return (
    <div className={`rounded-lg p-3 sm:p-4 space-y-3 ${isDark ? 'bg-white/[0.02] border border-white/[0.06]' : 'bg-white border border-gray-200 shadow-sm'}`}>
      {/* Row 1: Date pickers + Run button + Toggle */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
        {/* Khoảng thời gian */}
        <div className="flex gap-3 sm:gap-4" style={{ overflow: 'visible' }}>
          <div className="flex-1 sm:flex-none" style={{ overflow: 'visible' }}>
            <label className={`text-xs uppercase tracking-wider block mb-1.5 sm:mb-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Từ ngày</label>
            <div className="relative" style={{ overflow: 'visible' }}>
              <Calendar className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none z-10 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
              <DatePicker
                selected={fromDateObj}
                onChange={handleFromChange}
                isDark={isDark}
                className={`pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-full sm:w-44 ${isDark ? 'bg-white/[0.03] border-white/10 text-white focus:border-cyan-500/50' : 'bg-white border-gray-300 text-gray-900 focus:border-cyan-500'}`}
                placeholderText="Chọn ngày bắt đầu"
              />
            </div>
          </div>
          <div className="flex-1 sm:flex-none" style={{ overflow: 'visible' }}>
            <label className={`text-xs uppercase tracking-wider block mb-1.5 sm:mb-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Đến ngày</label>
            <div className="relative" style={{ overflow: 'visible' }}>
              <Calendar className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none z-10 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
              <DatePicker
                selected={toDateObj}
                onChange={handleToChange}
                isDark={isDark}
                className={`pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-full sm:w-44 ${isDark ? 'bg-white/[0.03] border-white/10 text-white focus:border-cyan-500/50' : 'bg-white border-gray-300 text-gray-900 focus:border-cyan-500'}`}
                placeholderText="Chọn ngày kết thúc"
              />
            </div>
          </div>
        </div>

        {/* Run button */}
        <div className="sm:self-end sm:pb-0.5">
          <button
            onClick={() => fetchDashboardData(true)}
            className="px-5 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2 transition-all bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg hover:shadow-cyan-500/25 hover:-translate-y-0.5"
          >
            <Play className="w-4 h-4" />
            Chạy report
          </button>
        </div>

        {/* Loại trừ At Home */}
        <div className="flex items-center sm:self-end sm:pb-1 relative">
          <button
            onClick={handleToggle}
            className={`flex items-center gap-2 text-sm transition-colors ${isDark ? 'text-white/40 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
          >
            {excludeAtHome ? (
              <ToggleRight className="w-6 h-6 text-cyan-400" />
            ) : (
              <ToggleLeft className={`w-6 h-6 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
            )}
            Loại trừ At Home
          </button>
          <AtHomeTooltip isDark={isDark} />
        </div>
      </div>

      {/* Row 2: Chọn nhanh tuần */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs mr-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Chọn nhanh:</span>
        {weekPresets.map((preset) => (
          <button
            key={preset.label}
            onClick={() => selectWeek(preset.monday, preset.sunday)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              isSelected(preset.monday, preset.sunday)
                ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
                : isDark
                  ? 'bg-white/[0.03] border-white/10 text-white/40 hover:text-white hover:bg-white/[0.06]'
                  : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Row 3: Tabs */}
      <div className={`inline-flex rounded-lg overflow-hidden border ${isDark ? 'border-white/[0.06]' : 'border-gray-200'}`}>
        {viewTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleViewChange(tab.key)}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
              activeView === tab.key
                ? 'bg-cyan-500 text-white'
                : isDark
                  ? 'bg-white/[0.03] text-white/40 hover:text-white hover:bg-white/[0.06]'
                  : 'bg-gray-50 text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function AtHomeTooltip({ isDark }: { isDark: boolean }) {
  const [visible, setVisible] = useState(false);
  return (
    <>
      <button
        className={`ml-1 transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible((v) => !v)}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {visible && (
        <div className={`absolute z-50 bottom-full left-0 mb-2 w-64 sm:w-72 p-3 rounded-lg border shadow-xl text-xs whitespace-pre-line leading-relaxed ${isDark ? 'bg-slate-900 border-slate-600 text-slate-200' : 'bg-white border-gray-200 text-gray-700'}`}>
          Khi bật, loại trừ khỏi thống kê:{'\n'}
          • Booking có BookingType = At Home (tập tại nhà){'\n'}
          • Booking thuộc studio có tên chứa "AT HOME"
          <div className={`absolute top-full left-4 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent ${isDark ? 'border-t-slate-600' : 'border-t-gray-200'}`} />
        </div>
      )}
    </>
  );
}
